const STAGING_HOST_SUFFIXES = ['staging.genufit.app', 'applicator-staging.pages.dev'] as const

/** Fallback when no email context is available (legacy tests / generic redirect). */
export const STAGING_ACCESS_DENIED_MESSAGE =
  'Staging is restricted. Contact the team if you need access.'

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

export function isStagingHost(hostname: string = window.location.hostname): boolean {
  const host = hostname.toLowerCase()
  return STAGING_HOST_SUFFIXES.some(
    (suffix) => host === suffix || host.endsWith(`.${suffix}`)
  )
}

export function getStagingAllowedEmails(): string[] {
  const raw = import.meta.env.VITE_STAGING_ALLOWED_EMAILS ?? ''
  return raw
    .split(',')
    .map((entry) => normalizeEmail(entry))
    .filter(Boolean)
}

export function isEmailAllowedOnStaging(email: string | null | undefined): boolean {
  if (!email) return false
  const allowed = getStagingAllowedEmails()
  if (allowed.length === 0) return true
  return allowed.includes(normalizeEmail(email))
}

export function stagingEmailDeniedMessage(email: string | null | undefined): string {
  const allowed = getStagingAllowedEmails()
  const addr = email?.trim()
  if (allowed.length === 0) {
    return STAGING_ACCESS_DENIED_MESSAGE
  }
  const preview =
    allowed.length <= 3 ? allowed.join(', ') : `${allowed.slice(0, 2).join(', ')}, …`
  if (addr) {
    return `Staging sign-in is limited to team emails. ${addr} is not on the allowlist (${preview}). Use a listed address or contact the team for access.`
  }
  return `Staging sign-in is limited to team emails. Your address is not on the allowlist (${preview}). Contact the team for access.`
}

export function assertStagingEmailAllowed(email: string | null | undefined): void {
  if (!isStagingHost()) return
  if (isEmailAllowedOnStaging(email)) return
  throw new Error(stagingEmailDeniedMessage(email))
}

/** @returns `true` when allowed, or a user-visible denial message after sign-out. */
export async function enforceStagingSessionAccess(
  getSession: () => Promise<{ data: { session: { user: { email?: string | null } } | null } }>,
  signOut: () => Promise<unknown>
): Promise<true | string> {
  if (!isStagingHost()) return true

  const { data: { session } } = await getSession()
  const email = session?.user?.email
  if (!session || isEmailAllowedOnStaging(email)) {
    return true
  }

  const message = stagingEmailDeniedMessage(email)
  await signOut()
  return message
}
